import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  getOrderPackageListAsync,
  getOrderLinesForPackageAsync,
  createOrderPackageAsync,
  updateOrderPackageAsync,
  deleteOrderPackageAsync,
  getOrderPackageDetailAsync,
} from '../actions/orderPackageActions';

// ===================================================================
// Types
// ===================================================================

interface OrderLineItem {
  id: number;
  type: string;
  name: string;
  sku: string;
  quantity: number;
  pending_qty: number;
  platform_product: { id: number; name: string };
  platform_product_variant: { id: number; sku: string; barcode: string | null; ean: string | null };
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

interface OrderPackageItem {
  id: number;
  order_id: number;
  is_fulfilled: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  line_items: any[];
  [key: string]: any;
}

interface PaginationState {
  pageNumber: number;
  count: number;
}

interface OrderPackageState {
  // Order-package list (history for a given order)
  loading: boolean;
  orderPackageList: OrderPackageItem[];
  pagination: PaginationState;

  // Order lines available for packaging (item picker)
  orderLinesLoading: boolean;
  orderLinesList: OrderLineItem[];
  orderLinesPagination: PaginationState;

  // Mutation loading (create / update / delete)
  mutationLoading: boolean;

  // Detail loading (fetch single package by id for edit hydration)
  detailLoading: boolean;
}

// ===================================================================
// Initial State
// ===================================================================

const initialState: OrderPackageState = {
  loading: false,
  orderPackageList: [],
  pagination: {
    pageNumber: 1,
    count: 0,
  },

  orderLinesLoading: false,
  orderLinesList: [],
  orderLinesPagination: {
    pageNumber: 1,
    count: 0,
  },

  mutationLoading: false,
  detailLoading: false,
};

// ===================================================================
// Slice
// ===================================================================

const orderPackageSlice = createSlice({
  name: 'orderPackageSlice',
  initialState,
  reducers: {
    // Reset order lines list (e.g. when closing the package drawer)
    resetOrderLinesList: (state) => {
      state.orderLinesList = [];
      state.orderLinesPagination = { pageNumber: 1, count: 0 };
      state.orderLinesLoading = false;
    },
    // Reset entire slice (e.g. when navigating away from order)
    resetOrderPackageState: () => initialState,
  },
  extraReducers: (builder) => {
    // ---------------------------------------------------------------
    // GET order-package list
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(getOrderPackageListAsync.pending), (state) => {
      state.loading = true;
    });
    builder.addMatcher(isAnyOf(getOrderPackageListAsync.rejected), (state) => {
      state.loading = false;
    });
    builder.addMatcher(isAnyOf(getOrderPackageListAsync.fulfilled), (state, action) => {
      state.loading = false;
      const res = action.payload;
      state.orderPackageList = res?.data ?? [];
      state.pagination.count = res?.data?.length ?? 0;
      state.pagination.pageNumber = action.meta.arg.page ?? 1;
    });

    // ---------------------------------------------------------------
    // GET order lines for package (item picker)
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(getOrderLinesForPackageAsync.pending), (state) => {
      state.orderLinesLoading = true;
    });
    builder.addMatcher(isAnyOf(getOrderLinesForPackageAsync.rejected), (state) => {
      state.orderLinesLoading = false;
    });
    builder.addMatcher(isAnyOf(getOrderLinesForPackageAsync.fulfilled), (state, action) => {
      state.orderLinesLoading = false;
      const res = action.payload;
      state.orderLinesList = res?.results?.data ?? [];
      state.orderLinesPagination.count = res?.count ?? 0;
      state.orderLinesPagination.pageNumber = action.meta.arg.page ?? 1;
    });

    // ---------------------------------------------------------------
    // CREATE order-package
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(createOrderPackageAsync.pending), (state) => {
      state.mutationLoading = true;
    });
    builder.addMatcher(
      isAnyOf(createOrderPackageAsync.rejected, createOrderPackageAsync.fulfilled),
      (state) => {
        state.mutationLoading = false;
      }
    );

    // ---------------------------------------------------------------
    // UPDATE order-package
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(updateOrderPackageAsync.pending), (state) => {
      state.mutationLoading = true;
    });
    builder.addMatcher(
      isAnyOf(updateOrderPackageAsync.rejected, updateOrderPackageAsync.fulfilled),
      (state) => {
        state.mutationLoading = false;
      }
    );

    // ---------------------------------------------------------------
    // DELETE order-package
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(deleteOrderPackageAsync.pending), (state) => {
      state.mutationLoading = true;
    });
    builder.addMatcher(
      isAnyOf(deleteOrderPackageAsync.rejected, deleteOrderPackageAsync.fulfilled),
      (state) => {
        state.mutationLoading = false;
      }
    );

    // ---------------------------------------------------------------
    // GET single order-package detail (for edit hydration)
    // ---------------------------------------------------------------
    builder.addMatcher(isAnyOf(getOrderPackageDetailAsync.pending), (state) => {
      state.detailLoading = true;
    });
    builder.addMatcher(
      isAnyOf(getOrderPackageDetailAsync.rejected, getOrderPackageDetailAsync.fulfilled),
      (state) => {
        state.detailLoading = false;
      }
    );
  },
});

export const { resetOrderLinesList, resetOrderPackageState } = orderPackageSlice.actions;
export default orderPackageSlice.reducer;
